/**
 * Centre de notifications : même historique et mêmes réglages que l'app. Le
 * catalogue des types vient du serveur (NotificationService), pour ne pas le
 * dupliquer ici.
 */
import React, { useEffect, useState } from 'react';

const API = '/api/notifications';
function h() { return { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`, 'Content-Type': 'application/json' }; }

interface Notification { id: number | string; title: string; body: string; read_at: string | null; created_at: string }
interface TypeNotif { key: string; label: string; urgent: boolean; enabled: boolean }
interface Preferences { types: TypeNotif[]; silence: { enabled: boolean; from: number; to: number } }

const dateLisible = (d: string) => new Date(d).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [prefs, setPrefs] = useState<Preferences | null>(null);
  const [reglages, setReglages] = useState(false);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(false);

  useEffect(() => {
    Promise.all([
      fetch(API, { headers: h() }).then(r => { if (!r.ok) throw new Error(); return r.json(); }),
      fetch(`${API}/preferences`, { headers: h() }).then(r => { if (!r.ok) throw new Error(); return r.json(); }),
    ])
      .then(([liste, p]) => { setNotifications(liste.notifications || []); setPrefs(p); })
      .catch(() => setErreur(true))
      .finally(() => setChargement(false));
  }, []);

  const marquerLues = async (ids?: Array<number | string>) => {
    const res = await fetch(`${API}/read`, { method: 'POST', headers: h(), body: JSON.stringify(ids ? { ids } : {}) });
    if (!res.ok) return;
    const maintenant = new Date().toISOString();
    setNotifications(n => n.map(x => (!ids || ids.includes(x.id)) ? { ...x, read_at: x.read_at || maintenant } : x));
  };

  // Enregistré à chaque changement : envoie seulement ce qui a bougé, le
  // serveur fusionne avec les réglages existants.
  const enregistrer = (corps: object) => fetch(`${API}/preferences`, { method: 'PUT', headers: h(), body: JSON.stringify(corps) });

  const basculerType = (t: TypeNotif) => {
    setPrefs(p => p && { ...p, types: p.types.map(x => x.key === t.key ? { ...x, enabled: !x.enabled } : x) });
    enregistrer({ types: { [t.key]: !t.enabled } });
  };

  const changerSilence = (modif: Partial<Preferences['silence']>) => {
    setPrefs(p => p && { ...p, silence: { ...p.silence, ...modif } });
    enregistrer({ silence: modif });
  };

  if (chargement) return <p className="text-sm text-gray-500">Chargement…</p>;
  if (erreur) return <p className="text-sm text-gray-500">Notifications indisponibles pour le moment.</p>;

  const nonLues = notifications.filter(n => !n.read_at).length;

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-gray-500">{nonLues > 0 ? `${nonLues} non lue${nonLues > 1 ? 's' : ''}` : 'Tout est lu'}</p>
        <div className="flex gap-2">
          {nonLues > 0 && (
            <button onClick={() => marquerLues()} className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 bg-white hover:bg-gray-50">Tout marquer comme lu</button>
          )}
          <button onClick={() => setReglages(!reglages)} className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 bg-white hover:bg-gray-50">Réglages</button>
        </div>
      </div>

      {reglages && prefs && (
        <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
          {prefs.types.map(t => (
            <label key={t.key} className="flex items-center justify-between gap-3 text-sm text-gray-800">
              <span>{t.label}{t.urgent && <span className="ml-2 text-xs text-amber-700">urgent, même la nuit</span>}</span>
              <input type="checkbox" checked={t.enabled} onChange={() => basculerType(t)} className="accent-violet-600 w-4 h-4" />
            </label>
          ))}
          <div className="pt-3 border-t border-gray-100 space-y-2">
            <label className="flex items-center justify-between gap-3 text-sm font-medium text-gray-800">
              Ne pas déranger la nuit
              <input type="checkbox" checked={prefs.silence.enabled} onChange={e => changerSilence({ enabled: e.target.checked })} className="accent-violet-600 w-4 h-4" />
            </label>
            {prefs.silence.enabled && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                De
                <select value={prefs.silence.from} onChange={e => changerSilence({ from: Number(e.target.value) })} className="border border-gray-200 rounded-lg px-2 py-1">
                  {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{i} h</option>)}
                </select>
                à
                <select value={prefs.silence.to} onChange={e => changerSilence({ to: Number(e.target.value) })} className="border border-gray-200 rounded-lg px-2 py-1">
                  {Array.from({ length: 24 }, (_, i) => <option key={i} value={i}>{i} h</option>)}
                </select>
              </div>
            )}
          </div>
        </div>
      )}

      {notifications.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-8 text-center text-sm text-gray-500">Aucune notification pour le moment.</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 divide-y divide-gray-100">
          {notifications.map(n => (
            <button key={n.id} onClick={() => !n.read_at && marquerLues([n.id])} className={`w-full text-left p-4 hover:bg-gray-50 ${n.read_at ? '' : 'bg-violet-50/50'}`}>
              <div className="flex items-start justify-between gap-3">
                <p className={`text-sm text-gray-900 ${n.read_at ? '' : 'font-semibold'}`}>{n.title}</p>
                <span className="text-xs text-gray-400 shrink-0">{dateLisible(n.created_at)}</span>
              </div>
              {n.body && <p className="text-sm text-gray-600 mt-1">{n.body}</p>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
